import { useNavigate } from "react-router"
import { useFilingMetrics } from "../../hooks/useFilingMetrics"
import { formatRejectionRate } from "../../lib/api/metrics"

export default function SaasMetrics() {
  const navigate = useNavigate()
  const { metrics, loading, error } = useFilingMetrics()

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
        <div className="flex items-center justify-center py-8">
          <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-brand-500 border-r-transparent"></div>
          <p className="ml-3 text-sm text-gray-600 dark:text-gray-400">Loading metrics...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
        <div className="rounded-lg bg-error-50 p-4 dark:bg-error-500/15">
          <p className="text-sm font-medium text-error-700 dark:text-error-400">
            Failed to load metrics: {error}
          </p>
        </div>
      </div>
    )
  }

  if (!metrics) {
    return null
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
      <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
            Overview
          </h3>
        </div>
      </div>
      <div className="grid rounded-2xl border border-gray-200 bg-white sm:grid-cols-2 xl:grid-cols-4 dark:border-gray-800 dark:bg-gray-900">
        {/* Requires Action */}
        <button
          onClick={() => navigate("/filing-tracker?status=revision_requested")}
          className="border-b border-gray-200 px-6 py-5 sm:border-r xl:border-b-0 dark:border-gray-800 text-left w-full hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors rounded-tl-2xl rounded-bl-2xl xl:rounded-bl-none"
        >
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Requires Action
          </span>
          <div className="mt-2 flex items-end gap-3">
            <h4 className="text-title-xs sm:text-title-sm font-bold text-gray-800 dark:text-white/90">
              {metrics.requires_action}
            </h4>
            <div>
              <span className={`flex items-center gap-1 rounded-full py-0.5 pr-2.5 pl-2 text-sm font-medium ${
                metrics.requires_action > 0
                  ? "bg-error-50 text-error-600 dark:bg-error-500/15 dark:text-error-500"
                  : "bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-500"
              }`}>
                {metrics.requires_action === 0 ? "✓ Clear" : `⚠ ${metrics.requires_action} Issues`}
              </span>
            </div>
          </div>
        </button>

        {/* Active Filings */}
        <div className="border-b border-gray-200 px-6 py-5 xl:border-r xl:border-b-0 dark:border-gray-800">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Active Filings
          </span>
          <div className="mt-2 flex items-end gap-3">
            <h4 className="text-title-xs sm:text-title-sm font-bold text-gray-800 dark:text-white/90">
              {metrics.active_filings}
            </h4>
            <div>
              <span className="bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-400 flex items-center gap-1 rounded-full py-0.5 pr-2.5 pl-2 text-sm font-medium">
                {metrics.total_submitted > 0 ? `${((metrics.active_filings / (metrics.total_submitted + metrics.active_filings)) * 100).toFixed(1)}%` : "—"}
              </span>
            </div>
          </div>
        </div>

        {/* Time to Submission */}
        <div className="border-b border-gray-200 px-6 py-5 sm:border-r sm:border-b-0 dark:border-gray-800">
          <div>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Avg Time to Submission
            </span>
            <div className="mt-2 flex items-end gap-3">
              <h4 className="text-title-xs sm:text-title-sm font-bold text-gray-800 dark:text-white/90">
                —
              </h4>
              <div>
                <span className="bg-info-50 text-info-600 dark:bg-info-500/15 dark:text-info-400 flex items-center gap-1 rounded-full py-0.5 pr-2.5 pl-2 text-sm font-medium">
                  {metrics.total_submitted > 0 ? `${metrics.total_submitted} submitted` : "—"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Rejection Rate */}
        <div className="px-6 py-5">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Rejection Rate
          </span>
          <div className="mt-2 flex items-end gap-3">
            <h4 className="text-title-xs sm:text-title-sm font-bold text-gray-800 dark:text-white/90">
              {formatRejectionRate(metrics.rejection_rate)}
            </h4>
            <div>
              <span className={`flex items-center gap-1 rounded-full py-0.5 pr-2.5 pl-2 text-sm font-medium ${
                metrics.rejection_rate < 5
                  ? "bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-500"
                  : metrics.rejection_rate < 15
                  ? "bg-warning-50 text-warning-600 dark:bg-warning-500/15 dark:text-warning-500"
                  : "bg-error-50 text-error-600 dark:bg-error-500/15 dark:text-error-500"
              }`}>
                {metrics.rejection_rate < 5 ? "✓ Good" : metrics.rejection_rate < 15 ? "⚠ Fair" : "✕ High"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
